import { useEffect } from 'react';
import { alpha, Box, Chip, CircularProgress, List, ListItemButton, ListItemText, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import ShortTextOutlined from '@mui/icons-material/ShortTextOutlined';
import { helpApi, type HelpSearchHit, type HelpSearchModule } from './helpApi';
import { byMenuOrder } from './helpIndex';
import { highlightParts } from './helpHighlight';
import type { HelpTarget } from './helpPanelStore';

interface HelpSearchResultsProps {
  query: string;
  entity?: string;
  language: string;
  /** The module the results are narrowed to; null for every module. */
  module: string | null;
  onFilter: (module: string | null) => void;
  onOpen: (target: HelpTarget) => void;
}

/**
 * The results of a help search (docs/15-designspecs/User-Help-And-Setup-Guides.md §Contextual help
 * panel): each page once, with its best-matching sections under it, and each field's help marked as a
 * field; every result names its module. When the results span modules, chips with each module's count
 * narrow them to one.
 */
export function HelpSearchResults({ query, entity, language, module, onFilter, onOpen }: HelpSearchResultsProps) {
  const { t } = useTranslation();
  const { data: result, isLoading } = useQuery({
    queryKey: ['help', 'search', query, entity, module, language],
    queryFn: () => helpApi.search(query, entity, module ?? undefined),
  });
  const modules = result?.modules ?? [];
  const filterGone = module !== null && result !== undefined && !modules.some((m) => m.module === module);

  // A module the new search does not reach would leave the list empty with no chip to undo it.
  useEffect(() => {
    if (filterGone) onFilter(null);
  }, [filterGone, onFilter]);

  if (isLoading) return <CircularProgress size={20} />;
  return (
    <>
      {modules.length > 1 && <ModuleChips modules={modules} selected={module} onFilter={onFilter} />}
      {result && result.hits.length > 0 ? (
        <List dense disablePadding>
          {result.hits.map((hit) =>
            hit.kind === 'page' ? (
              <PageResult key={hit.id} hit={hit} query={query} onOpen={onOpen} />
            ) : (
              <FieldResult key={hit.id} hit={hit} query={query} onOpen={onOpen} />
            ),
          )}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary">{t('help.noResults')}</Typography>
      )}
    </>
  );
}

interface ModuleChipsProps {
  modules: HelpSearchModule[];
  selected: string | null;
  onFilter: (module: string | null) => void;
}

function ModuleChips({ modules, selected, onFilter }: ModuleChipsProps) {
  const { t } = useTranslation();
  const total = modules.reduce((sum, m) => sum + m.count, 0);
  const ordered = [...modules].sort((a, b) => byMenuOrder(a.module, b.module));
  return (
    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }} role="group" aria-label={t('help.modules')}>
      <Chip
        size="small"
        label={`${t('help.allModules')} ${total}`}
        color={selected === null ? 'primary' : 'default'}
        variant={selected === null ? 'filled' : 'outlined'}
        aria-pressed={selected === null}
        onClick={() => onFilter(null)}
      />
      {ordered.map((m) => (
        <Chip
          key={m.module}
          size="small"
          label={`${m.label} ${m.count}`}
          color={selected === m.module ? 'primary' : 'default'}
          variant={selected === m.module ? 'filled' : 'outlined'}
          aria-pressed={selected === m.module}
          onClick={() => onFilter(m.module)}
        />
      ))}
    </Stack>
  );
}

interface ResultProps {
  hit: HelpSearchHit;
  /** The search, whose words are marked in the result. */
  query: string;
  onOpen: (target: HelpTarget) => void;
}

/** The text with the words the search matched marked. */
function Highlighted({ text, query }: { text: string; query: string }) {
  return (
    <>
      {highlightParts(text, query).map((part, i) =>
        part.match ? (
          <Box
            component="mark"
            key={i}
            sx={(theme) => ({ bgcolor: alpha(theme.palette.warning.main, 0.28), color: 'inherit', borderRadius: 0.5, px: 0.125 })}
          >
            {part.text}
          </Box>
        ) : (
          part.text
        ),
      )}
    </>
  );
}

/** Two lines of a snippet at most, so a result stays one glance long. */
const TWO_LINES = { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' } as const;

/** Each result is a card of its own, so a section can never be mistaken for another page's. */
const CARD = { listStyle: 'none', mb: 1.5, border: 1, borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' } as const;

interface ResultHeaderProps {
  title: string;
  moduleLabel: string;
  query: string;
}

function ResultHeader({ title, moduleLabel, query }: ResultHeaderProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flex: 1, minWidth: 0 }}>
      <Typography component="span" variant="subtitle2" sx={{ flex: 1, fontWeight: 600 }}>
        <Highlighted text={title} query={query} />
      </Typography>
      <Typography component="span" variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
        {moduleLabel}
      </Typography>
    </Box>
  );
}

/** A page once: its title row opens it, each matching section under it opens the page at that section. */
function PageResult({ hit, query, onOpen }: ResultProps) {
  return (
    <Box component="li" sx={CARD}>
      <ListItemButton onClick={() => onOpen({ pageId: hit.id })} sx={{ bgcolor: 'action.hover', gap: 1, py: 0.75 }}>
        <DescriptionOutlined fontSize="small" color="action" />
        <ResultHeader title={hit.title} moduleLabel={hit.moduleLabel} query={query} />
      </ListItemButton>
      <List dense disablePadding sx={{ py: 0.5 }}>
        {hit.sections.map((section) => (
          <ListItemButton
            key={section.id}
            onClick={() => onOpen({ pageId: hit.id, anchor: section.anchor })}
            sx={{
              alignItems: 'flex-start',
              ml: 2.75,
              pl: 1.5,
              borderLeft: 2,
              borderColor: 'divider',
              '&:hover': { borderColor: 'primary.main' },
            }}
          >
            <ListItemText
              primary={<Highlighted text={section.heading} query={query} />}
              secondary={<Highlighted text={section.snippet} query={query} />}
              slotProps={{ primary: { variant: 'body2', sx: { fontWeight: 500 } }, secondary: { variant: 'caption', sx: TWO_LINES } }}
            />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );
}

/** A field's help, marked as a field of its entity. */
function FieldResult({ hit, query, onOpen }: ResultProps) {
  const { t } = useTranslation();
  return (
    <Box component="li" sx={CARD}>
      <ListItemButton onClick={() => onOpen({ fieldId: hit.id })} sx={{ alignItems: 'flex-start', gap: 1, py: 0.75 }}>
        <ShortTextOutlined fontSize="small" color="action" sx={{ mt: 0.25 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ResultHeader title={hit.title} moduleLabel={hit.moduleLabel} query={query} />
          <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
            {t('help.fieldOf', { entity: hit.entityLabel })}
          </Typography>
          <Typography component="span" variant="caption" color="text.secondary" sx={TWO_LINES}>
            <Highlighted text={hit.snippet} query={query} />
          </Typography>
        </Box>
      </ListItemButton>
    </Box>
  );
}
